import React, { useState, useEffect } from 'react';
import { adminAPI, commonAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';

const AssessmentConfig = () => {
  const [assessmentFields, setAssessmentFields] = useState([]);
  const [academicSessions, setAcademicSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [loading, setLoading] = useState(true);

  const fieldSchema = Yup.object().shape({
    field_name: Yup.string().required('Field name is required'),
    field_code: Yup.string()
      .required('Field code is required')
      .matches(/^[a-z0-9_]+$/, 'Only lowercase letters, numbers, and underscores allowed'),
    display_name: Yup.string().required('Display name is required'),
    field_type: Yup.string().required('Field type is required'),
    max_score: Yup.number()
      .required('Max score is required')
      .min(0, 'Must be at least 0')
      .max(1000, 'Cannot exceed 1000'),
    min_score: Yup.number()
      .required('Min score is required')
      .min(0, 'Must be at least 0')
      .max(Yup.ref('max_score'), 'Cannot exceed max score'),
    weight: Yup.number()
      .required('Weight is required')
      .min(0, 'Must be at least 0')
      .max(10, 'Cannot exceed 10'),
    is_compulsory: Yup.boolean(),
    display_order: Yup.number().required('Display order is required'),
  });

  const configSchema = Yup.object().shape({
    academic_session_id: Yup.number().required('Academic session is required'),
    term_id: Yup.number().required('Term is required'),
    config_name: Yup.string().required('Configuration name is required'),
    // config_data: Yup.object().shape({
    //   enabled_fields: Yup.array().min(1, 'Select at least one field'),
    //   field_weights: Yup.object(),
    // }),
    config_data: Yup.object({
  enabled_fields: Yup.array()
    .of(Yup.string())
    .min(1, 'Select at least one field')
    .required(),
  field_weights: Yup.object().required(),
}),


  });



  useEffect(() => {
    fetchData();
  }, []);

  // Add this useEffect in the main component (outside the Formik)
useEffect(() => {
  if (showConfigModal && currentConfig) {
    // You can handle automatic weight syncing here
    // Or better yet, handle it in the onChange handlers as shown above
  }
}, [showConfigModal, currentConfig]);




  // In fetchData function, change:
const fetchData = async () => {
  try {
    setLoading(true);
    const [fieldsRes, sessionsRes] = await Promise.all([
      adminAPI.getAssessmentFields(),
      adminAPI.getAcademicSessions(), // Changed from commonAPI
    ]);
    
    setAssessmentFields(fieldsRes.data);
    setAcademicSessions(sessionsRes.data);
  } catch (error) {
    toast.error('Failed to load data');
  } finally {
    setLoading(false);
  }
};

// Update handleSaveConfig to use the returned data:
const handleSaveConfig = async (values, { setSubmitting, resetForm }) => {
  try {
    const response = await adminAPI.saveAssessmentConfig(values);
    toast.success('Configuration saved successfully');
    
    // If you need to use the returned config data
    console.log('Saved config:', response.data.config);
    
    setShowConfigModal(false);
    resetForm();
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Operation failed...');
  } finally {
    setSubmitting(false);
  }
};

  const handleLoadTerms = async (sessionId) => {
    try {
      const response = await commonAPI.getTerms(sessionId);
      setTerms(response.data);
    } catch (error) {
      toast.error('Failed to load terms');
    }
  };

  const handleSaveField = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingField) {
        await adminAPI.updateAssessmentField(editingField.id, values);
        toast.success('Field updated successfully');
      } else {
        await adminAPI.createAssessmentField(values);
        toast.success('Field created successfully');
      }
      
      setShowFieldModal(false);
      resetForm();
      setEditingField(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };


  const handleLoadConfig = async (sessionId, termId) => {
    try {
      const response = await adminAPI.getAssessmentConfig(sessionId, termId);
      setCurrentConfig(response.data);
      setShowConfigModal(true);
    } catch (error) {
      // Create new config if none exists
      setCurrentConfig({
        academic_session_id: sessionId,
        term_id: termId,
        config_name: `Config for Session ${sessionId}, Term ${termId}`,
        config_data: {
          enabled_fields: assessmentFields.filter(f => f.is_active).map(f => f.field_code),
          field_weights: Object.fromEntries(
            assessmentFields.filter(f => f.is_active).map(f => [f.field_code, f.weight])
          ),
        },
      });
      setShowConfigModal(true);
    }
  };

  const handleDeleteField = async (id) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      try {
        await adminAPI.deleteAssessmentField(id);
        toast.success('Field deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete field');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Assessment Configuration</h1>
          <p className="text-gray-600">Configure dynamic assessment fields and their weights</p>
        </div>
        <div className="space-x-3">
          <button
            onClick={() => {
              setEditingField(null);
              setShowFieldModal(true);
            }}
            className="btn-primary flex items-center"
          >
            <i className="fas fa-plus mr-2"></i> Add Field
          </button>
          {/* <button
            onClick={() => setShowConfigModal(true)}
            className="btn-secondary flex items-center"
          >
            <i className="fas fa-cog mr-2"></i> Configure Session
          </button> */}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Fields</p>
              <p className="text-2xl font-bold">{assessmentFields.length}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <i className="fas fa-list text-primary-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Fields</p>
              <p className="text-2xl font-bold">
                {assessmentFields.filter(f => f.is_active).length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-check-circle text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Compulsory Fields</p>
              <p className="text-2xl font-bold">
                {assessmentFields.filter(f => f.is_compulsory).length}
              </p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <i className="fas fa-exclamation-circle text-yellow-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Field Types</p>
              <p className="text-2xl font-bold">
                {[...new Set(assessmentFields.map(f => f.field_type))].length}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-tags text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Fields Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800">Assessment Fields</h3>
          <p className="text-gray-600 text-sm">Manage all assessment field types</p>
        </div>
        
        <div className="table-responsive">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Field
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type & Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scores & Weight
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {assessmentFields.map((field) => (
                <tr key={field.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {field.display_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {field.field_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {field.field_type}
                      </span>
                      <div className="text-sm text-gray-600 font-mono">
                        {field.field_code}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm">
                        <span className="text-gray-500">Score: </span>
                        <span className="font-medium">
                          {field.min_score} - {field.max_score}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Weight: </span>
                        <span className="font-medium">{field.weight}x</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Order: </span>
                        <span className="font-medium">{field.display_order}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        field.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {field.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        field.is_compulsory
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {field.is_compulsory ? 'Compulsory' : 'Optional'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium space-x-2">
                    <button
                      onClick={() => {
                        setEditingField(field);
                        setShowFieldModal(true);
                      }}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteField(field.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Session Configurations */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Session Configurations</h3>
        <div className="space-y-4">
          {academicSessions.map((session) => (
            <div key={session.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">{session.academic_session}</h4>
                  <p className="text-sm text-gray-500">
                    Status: <span className={`font-medium ${
                      session.academic_session_status === 'published'
                        ? 'text-green-600'
                        : 'text-yellow-600'
                    }`}>
                      {session.academic_session_status}
                    </span>
                  </p>
                </div>
                <button
                  onClick={() => handleLoadTerms(session.id)}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  <i className="fas fa-chevron-down mr-1"></i> Show Terms
                </button>
              </div>
              
              {terms.filter(t => t.academic_session_id === session.id).length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  {terms
                    .filter(t => t.academic_session_id === session.id)
                    .map((term) => (
                      <div key={term.id} className="border rounded p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{term.term}</p>
                            <p className="text-xs text-gray-500">Term {term.id}</p>
                          </div>
                          <button
                            onClick={() => handleLoadConfig(session.id, term.id)}
                            className="text-sm bg-primary-50 text-primary-700 hover:bg-primary-100 px-3 py-1 rounded"
                          >
                            Configure
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Field Modal */}
      <Modal
        isOpen={showFieldModal}
        onClose={() => {
          setShowFieldModal(false);
          setEditingField(null);
        }}
        title={editingField ? 'Edit Assessment Field' : 'Create Assessment Field'}
        size="lg"
      >
        <Formik
          initialValues={{
            field_name: editingField?.field_name || '',
            field_code: editingField?.field_code || '',
            display_name: editingField?.display_name || '',
            field_type: editingField?.field_type || 'ca',
            max_score: editingField?.max_score || 100,
            min_score: editingField?.min_score || 0,
            weight: editingField?.weight || 1.0,
            is_compulsory: editingField?.is_compulsory || false,
            display_order: editingField?.display_order || 0,
            is_active: editingField?.is_active ?? true,
          }}
          validationSchema={fieldSchema}
          onSubmit={handleSaveField}
        >
          {({ isSubmitting, errors, touched, values }) => (
            <Form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Name *
                  </label>
                  <Field
                    type="text"
                    name="field_name"
                    className="input-field"
                    placeholder="e.g., First CA"
                  />
                  {errors.field_name && touched.field_name && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_name}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Code *
                  </label>
                  <Field
                    type="text"
                    name="field_code"
                    className="input-field font-mono"
                    placeholder="e.g., ca1"
                  />
                  {errors.field_code && touched.field_code && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_code}</div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Lowercase letters, numbers, and underscores only
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <Field
                  type="text"
                  name="display_name"
                  className="input-field"
                  placeholder="e.g., First Continuous Assessment"
                />
                {errors.display_name && touched.display_name && (
                  <div className="text-red-500 text-sm mt-1">{errors.display_name}</div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Field Type *
                  </label>
                  <Field as="select" name="field_type" className="input-field">
                    <option value="ca">Continuous Assessment (CA)</option>
                    <option value="exam">Examination</option>
                    <option value="assignment">Assignment</option>
                    <option value="project">Project</option>
                    <option value="practical">Practical</option>
                    <option value="other">Other</option>
                  </Field>
                  {errors.field_type && touched.field_type && (
                    <div className="text-red-500 text-sm mt-1">{errors.field_type}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order *
                  </label>
                  <Field
                    type="number"
                    name="display_order"
                    className="input-field"
                    min="0"
                  />
                  {errors.display_order && touched.display_order && (
                    <div className="text-red-500 text-sm mt-1">{errors.display_order}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Minimum Score *
                  </label>
                  <Field
                    type="number"
                    name="min_score"
                    className="input-field"
                    step="0.1"
                    min="0"
                  />
                  {errors.min_score && touched.min_score && (
                    <div className="text-red-500 text-sm mt-1">{errors.min_score}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maximum Score *
                  </label>
                  <Field
                    type="number"
                    name="max_score"
                    className="input-field"
                    step="0.1"
                    min={values.min_score}
                  />
                  {errors.max_score && touched.max_score && (
                    <div className="text-red-500 text-sm mt-1">{errors.max_score}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight *
                  </label>
                  <Field
                    type="number"
                    name="weight"
                    className="input-field"
                    step="0.1"
                    min="0"
                    max="10"
                  />
                  {errors.weight && touched.weight && (
                    <div className="text-red-500 text-sm mt-1">{errors.weight}</div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Multiplier for final score calculation
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <Field
                    type="checkbox"
                    name="is_compulsory"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_compulsory" className="ml-2 text-sm text-gray-700">
                    Compulsory Field
                  </label>
                </div>

                <div className="flex items-center">
                  <Field
                    type="checkbox"
                    name="is_active"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Active Field
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFieldModal(false);
                    setEditingField(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingField ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      
      {/* Config Modal */}
<Modal
  isOpen={showConfigModal}
  onClose={() => {
    setShowConfigModal(false);
    setCurrentConfig(null);
  }}
  title="Configure Assessment Fields for Session"
  size="lg"
>
  {currentConfig && (
    <Formik
      enableReinitialize
      initialValues={{
        academic_session_id: currentConfig.academic_session_id || '',
        term_id: currentConfig.term_id || '',
        config_name: currentConfig.config_name || '',
        config_data: {
          enabled_fields: currentConfig.config_data?.enabled_fields || [],
          field_weights: currentConfig.config_data?.field_weights || {},
        },
      }}
      validationSchema={configSchema}
      onSubmit={handleSaveConfig}
    >
      {({ isSubmitting, errors, touched, values, setFieldValue }) => {
        // Move the sync logic to a separate function
        const syncFieldWeights = () => {
          const updatedWeights = { ...values.config_data.field_weights };
          values.config_data.enabled_fields?.forEach(fieldCode => {
            if (!updatedWeights[fieldCode]) {
              const field = assessmentFields.find(f => f.field_code === fieldCode);
              updatedWeights[fieldCode] = field?.weight || 1.0;
            }
          });
          
          // Remove weights for disabled fields
          Object.keys(updatedWeights).forEach(key => {
            if (!values.config_data.enabled_fields?.includes(key)) {
              delete updatedWeights[key];
            }
          });
          
          if (JSON.stringify(updatedWeights) !== JSON.stringify(values.config_data.field_weights)) {
            setFieldValue('config_data.field_weights', updatedWeights);
          }
        };

        return (
          <Form className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Academic Session *
                </label>
                <Field as="select" name="academic_session_id" className="input-field" disabled>
                  <option value="">Select Session</option>
                  {academicSessions.map((session) => (
                    <option key={session.id} value={session.id}>
                      {session.academic_session}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Term *
                </label>
                <Field as="select" name="term_id" className="input-field" disabled>
                  <option value="">Select Term</option>
                  {terms
                    .filter(t => t.academic_session_id === values.academic_session_id)
                    .map((term) => (
                      <option key={term.id} value={term.id}>
                        {term.term}
                      </option>
                    ))}
                </Field>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Configuration Name *
              </label>
              <Field
                type="text"
                name="config_name"
                className="input-field"
                placeholder="e.g., 2023-2024 Term 1 Configuration"
              />
              {errors.config_name && touched.config_name && (
                <div className="text-red-500 text-sm mt-1">{errors.config_name}</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Assessment Fields *
              </label>
              <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                {assessmentFields.filter(f => f.is_active).map((field) => {
                  const isEnabled = values.config_data?.enabled_fields?.includes(field.field_code);
                  const weightValue = values.config_data?.field_weights?.[field.field_code] || field.weight;
                  
                  return (
                    <div key={field.id} className="flex items-center justify-between mb-3 last:mb-0 p-2 hover:bg-gray-50 rounded">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={(e) => {
                            const enabled = values.config_data?.enabled_fields || [];
                            if (e.target.checked) {
                              const newEnabled = [...enabled, field.field_code];
                              setFieldValue('config_data.enabled_fields', newEnabled);
                              // Set default weight when enabling
                              setFieldValue(`config_data.field_weights.${field.field_code}`, field.weight);
                            } else {
                              const newEnabled = enabled.filter(f => f !== field.field_code);
                              setFieldValue('config_data.enabled_fields', newEnabled);
                              syncFieldWeights(); // Sync after disabling
                            }
                          }}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{field.display_name}</div>
                          <div className="text-sm text-gray-500">
                            {field.field_code} • {field.field_type} • {field.min_score}-{field.max_score} pts
                          </div>
                        </div>
                      </div>
                      <div className="w-24">
                        <Field
                          type="number"
                          name={`config_data.field_weights.${field.field_code}`}
                          className={`input-field text-sm py-1 ${!isEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                          step="0.1"
                          min="0"
                          max="10"
                          disabled={!isEnabled}
                          placeholder="Weight"
                          onBlur={() => syncFieldWeights()} // Sync on blur
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {errors.config_data?.enabled_fields && (
                <div className="text-red-500 text-sm mt-1">{errors.config_data.enabled_fields}</div>
              )}
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Selected Fields Preview</h4>
              <div className="space-y-2">
                {values.config_data?.enabled_fields?.length > 0 ? (
                  values.config_data.enabled_fields.map((fieldCode) => {
                    const field = assessmentFields.find(f => f.field_code === fieldCode);
                    return field ? (
                      <div key={fieldCode} className="flex justify-between items-center text-sm py-2 border-b last:border-0">
                        <span className="font-medium">{field.display_name}</span>
                        <span className="text-gray-600">
                          Weight: {values.config_data?.field_weights?.[fieldCode] || field.weight}x
                        </span>
                      </div>
                    ) : null;
                  })
                ) : (
                  <p className="text-gray-500 text-sm italic">No fields selected</p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowConfigModal(false);
                  setCurrentConfig(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              {/* <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary px-4 py-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save mr-2"></i>
                    Save Configuration
                  </>
                )}
              </button> */}
            </div>
          </Form>
        );
      }}
    </Formik>
  )}
</Modal>
    </div>
  );
};

export default AssessmentConfig;